import React from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { cantieri } from '../../utils/api';
import { formatDate } from '../../utils/date';
import CantiereActivityFeed from './CantiereActivityFeed';
import PanoramicaSintesiPDF from './PanoramicaSintesiPDF';
import CantierePDFCompleto from './CantierePDFCompleto';
import FeatureGate from '../shared/FeatureGate';  // CAP-008
import UpsellBlock from '../shared/UpsellBlock';  // CAP-008
import { useAuth } from '../../context/AuthContext';

const slugify = s => s.toLowerCase().replace(/[àáâãäå]/g,'a').replace(/[èéêë]/g,'e').replace(/[ìíîï]/g,'i').replace(/[òóôõö]/g,'o').replace(/[ùúûü]/g,'u').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

const fmtEur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtShort = (d) => new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });

const STATUS_COLORS = {
  planned: '#F0AD4E', in_progress: '#4A7CFF', at_risk: '#F5A623',
  done: '#2EA084', blocked: '#E85D5D',
};
const STATUS_LABEL = {
  planned: 'Pianificata', in_progress: 'In corso', at_risk: 'A rischio',
  done: 'Completata', blocked: 'Bloccata',
};

export default function PanoramicaTab({ data, cantiereId, onSwitchTab, onRefresh, fetchPDFCompleto, pdfData, loadingPDF }) {
  const { hasFeature } = useAuth();  // CAP-008 — nasconde quick-action foto e PDF completo se il piano non li include
  const canExportPdf = hasFeature('export_cantieri_pdf');
  if (!data) return null;

  const c = data.contatori || {};
  const p = data.piano_lavori || {};
  const b = data.budget || {};
  const ur = data.ultimo_report;
  const fasi = p.fasi || [];

  const alerts = [];
  if (c.fatture_aperte > 0) alerts.push('Hai ' + c.fatture_aperte + ' fatture aperte da incassare');
  if (p.fasi_a_rischio > 0) alerts.push(p.fasi_a_rischio + ' fasi del piano sono a rischio');

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
      {/* Colonna principale */}
      <div style={{ flex: '1 1 500px', minWidth: 300 }}>
        <AlertCard items={alerts} />

        {/* KPI */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20 }}>
          <KpiCard icona="📋" label="Avanzamento" valore={p.esiste ? p.percentuale_avanzamento + '%' : '—'} colore="var(--brand)" onClick={() => onSwitchTab && onSwitchTab('piano')} />
          <KpiCard icona="🧾" label="Fatture aperte" valore={c.fatture_aperte} colore={c.fatture_aperte > 0 ? 'var(--warn)' : undefined} onClick={() => onSwitchTab && onSwitchTab('fatture')} />
          <KpiCard icona="📁" label="Documenti" valore={c.documenti} onClick={() => onSwitchTab && onSwitchTab('documenti')} />
          <KpiCard icona="🎙️" label="Vocali" valore={c.vocali_non_letti} onClick={() => onSwitchTab && onSwitchTab('report')} />
        </div>

        {/* Gantt mini */}
        {p.esiste && fasi.length > 0 && <GanttMini fasi={fasi} onSwitchTab={onSwitchTab} />}

        {/* Azioni rapide */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 10, color: 'var(--ds-text)' }}>
            ⚡ Azioni rapide
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { label: 'Nuovo report', icona: '📊', tab: 'report' },
              { label: 'Carica documento', icona: '📁', tab: 'documenti' },
              { label: 'Piano lavori', icona: '📋', tab: 'piano' },
              { label: 'Aggiungi foto', icona: '📸', tab: 'foto' },
            ].filter(a => a.tab !== 'foto' || hasFeature('upload_foto_cantiere')).map((a, i) => (
              <button key={i} onClick={() => onSwitchTab && onSwitchTab(a.tab)} style={{
                padding: '10px 18px', borderRadius: 'var(--radius-base)',
                border: '1px solid var(--ds-border)',
                background: 'var(--ds-bg)', cursor: 'pointer',
                fontSize: 13, color: 'var(--ds-text)', fontWeight: 400,
                display: 'flex', alignItems: 'center', gap: 8,
                minHeight: 44, transition: 'background 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--chip)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--ds-bg)'}
              >
                <span>{a.icona}</span> {a.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bottoni PDF */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <PDFDownloadLink
            document={<PanoramicaSintesiPDF data={data} />}
            fileName={'scheda-cantiere-' + slugify(data?.cantiere?.titolo || 'cantiere') + '-' + new Date().toISOString().slice(0, 10) + '.pdf'}
          >
            {({ loading }) => (
              <button style={{ padding: '10px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 13, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
                📄 {loading ? 'Preparazione...' : 'PDF Sintesi'}
              </button>
            )}
          </PDFDownloadLink>
          <FeatureGate flag="export_cantieri_pdf" hideWhenOff>
            {pdfData ? (
              <PDFDownloadLink
                document={<CantierePDFCompleto data={data} piano={pdfData.piano} reports={pdfData.reports} documenti={pdfData.documenti} spese={pdfData.spese} foto={pdfData.foto} />}
                fileName={'dossier-cantiere-' + slugify(data?.cantiere?.titolo || 'cantiere') + '-' + new Date().toISOString().slice(0, 10) + '.pdf'}
              >
                {({ loading }) => (
                  <button style={{ padding: '10px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 13, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
                    📦 {loading ? 'Generazione...' : 'PDF Completo'}
                  </button>
                )}
              </PDFDownloadLink>
            ) : (
              <button
                onClick={fetchPDFCompleto}
                disabled={loadingPDF}
                style={{ padding: '10px 18px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: loadingPDF ? 'wait' : 'pointer', fontSize: 13, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, opacity: loadingPDF ? 0.6 : 1 }}
              >
                📦 {loadingPDF ? 'Caricamento dati...' : 'PDF Completo'}
              </button>
            )}
          </FeatureGate>
        </div>

        {!canExportPdf && (
          <UpsellBlock
            titolo="PDF Completo del cantiere"
            messaggio="Il dossier PDF completo (piano, report, documenti, spese e foto) è incluso nei piani a pagamento — passa a Smart, Business o Pro per generarlo."
          />
        )}

        {/* Activity feed — max 10, "Vedi tutte" espande */}
        <CantiereActivityFeed feed={(data.activity_feed || []).slice(0, 10)} onViewAll={() => {}} onNavigateTab={onSwitchTab} />
      </div>

      {/* Sidebar destra */}
      <div style={{ flex: '0 0 280px', minWidth: 240 }}>
        <SidebarCard title="📌 Dettagli">
          <InfoRow label="Cliente" value={data.cantiere?.cliente_nome} />
          <InfoRow label="Indirizzo" value={data.cantiere?.indirizzo} />
          <InfoRow label="Inizio" value={formatDate(data.cantiere?.data_inizio)} />
          <InfoRow label="Fine stimata" value={formatDate(data.cantiere?.data_fine_stimata)} />
        </SidebarCard>

        {(p.esiste || b.valore_commessa > 0) && (
          <SidebarCard title="💰 Budget">
            {/* Sezione piano */}
            {p.esiste && (
              <>
                <InfoRow label="Budget piano" value={fmtEur(b.budget_totale)} />
                <InfoRow label="Speso" value={fmtEur(b.budget_speso)} color={b.budget_totale > 0 && b.budget_speso > b.budget_totale * 0.8 ? 'var(--ds-danger)' : undefined} />
                {b.budget_totale > 0 && (
                  <div style={{ marginTop: 6, marginBottom: 6, height: 6, borderRadius: 3, background: 'var(--ds-border)' }}>
                    <div style={{
                      height: '100%', borderRadius: 3,
                      background: b.budget_speso > b.budget_totale ? 'var(--ds-danger)' : 'var(--brand)',
                      width: Math.min(100, (b.budget_speso / b.budget_totale) * 100) + '%',
                    }} />
                  </div>
                )}
              </>
            )}
            {/* Sezione fatturazione — solo se valore_commessa > 0 */}
            {b.valore_commessa > 0 && (
              <>
                {p.esiste && <div style={{ borderTop: '1px solid var(--ds-border)', margin: '8px 0' }} />}
                <InfoRow label="Valore commessa" value={fmtEur(b.valore_commessa)} />
                <InfoRow label="Fatturato" value={fmtEur(b.fatturato)} color="var(--brand)" />
                <InfoRow label="Da incassare" value={fmtEur(b.da_incassare)} color="var(--warn)" />
                <InfoRow label="Residuo" value={fmtEur(b.residuo_da_fatturare)} color="var(--ds-muted)" />
              </>
            )}
          </SidebarCard>
        )}

        {ur && (
          <SidebarCard
            title="📊 Ultimo report"
            action={<button onClick={() => onSwitchTab && onSwitchTab('report')} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--brand)' }}>Vedi tutti →</button>}
            onClick={() => onSwitchTab && onSwitchTab('report')}
          >
            <InfoRow label="Data" value={formatDate(ur.data)} />
            <InfoRow label="Avanzamento" value={ur.avanzamento_sal + '%'} />
            <InfoRow label="Problemi aperti" value={ur.problemi_aperti} />
          </SidebarCard>
        )}

        {data.milestone && data.milestone.length > 0 && (
          <SidebarCard title="🏁 Milestone">
            {data.milestone.map(m => (
              <MilestoneRow key={m.id} milestone={m} cantiereId={cantiereId} onRefresh={onRefresh} />
            ))}
          </SidebarCard>
        )}

        {/* Ultimi vocali — sotto milestone */}
        {data.ultimi_vocali && data.ultimi_vocali.length > 0 && (
          <SidebarCard title="🎙️ Ultimi vocali" action={<button onClick={() => onSwitchTab && onSwitchTab('report')} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--brand)' }}>Tutti →</button>}>
            {data.ultimi_vocali.map((v, i) => {
              const dt = new Date(v.created_at);
              const isToday = dt.toDateString() === new Date().toDateString();
              const isYesterday = dt.toDateString() === new Date(Date.now() - 86400000).toDateString();
              const dayLabel = isToday ? 'Oggi' : isYesterday ? 'Ieri' : fmtShort(v.created_at);
              const timeLabel = dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
              return (
                <div key={v.id || i} style={{
                  padding: '8px 0',
                  borderBottom: i < data.ultimi_vocali.length - 1 ? '1px solid var(--ds-border)' : 'none',
                }}>
                  <div style={{ fontSize: 10, color: 'var(--ds-muted)', marginBottom: 3 }}>
                    {dayLabel} {timeLabel}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ds-text)', fontStyle: 'italic', lineHeight: 1.4 }}>
                    "{v.contenuto}"
                  </div>
                </div>
              );
            })}
          </SidebarCard>
        )}
      </div>
    </div>
  );
}

// ── Gantt Mini (timeline) ──────────────────────────────────────────────────

function GanttMini({ fasi, onSwitchTab }) {
  const dates = fasi.flatMap(f => [
    new Date(f.data_inizio_pianificata).getTime(),
    new Date(f.data_fine_pianificata).getTime(),
  ]);
  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);
  const range = maxDate - minDate || 1;
  const todayTs = new Date().setHours(0, 0, 0, 0);
  const todayPct = Math.min(100, Math.max(0, ((todayTs - minDate) / range) * 100));

  return (
    <div style={{ marginBottom: 20, padding: 16, borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', background: 'var(--chip)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 500 }}>🔨 Piano lavori</div>
        <button onClick={() => onSwitchTab && onSwitchTab('piano')} style={linkBtn}>Dettagli →</button>
      </div>
      <div style={{ position: 'relative' }}>
        {todayPct > 0 && todayPct < 100 && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: todayPct + '%', width: 2, background: 'var(--ds-danger)', zIndex: 2, opacity: 0.5, borderRadius: 1 }}>
            <span style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', fontSize: 9, color: 'var(--ds-danger)', fontWeight: 500 }}>Oggi</span>
          </div>
        )}
        {fasi.map(fase => {
          const start = new Date(fase.data_inizio_pianificata).getTime();
          const end = new Date(fase.data_fine_pianificata).getTime();
          const leftPct = ((start - minDate) / range) * 100;
          const widthPct = Math.max(5, ((end - start) / range) * 100);
          const colore = STATUS_COLORS[fase.status] || '#CBD5E0';
          const pct = fase.percentuale_completamento;
          return (
            <div key={fase.id} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--ds-text)' }}>{fase.nome}</span>
                  <span style={{ fontSize: 10, color: 'var(--ds-muted)' }}>{fmtShort(fase.data_inizio_pianificata)} → {fmtShort(fase.data_fine_pianificata)}</span>
                  {fase.ritardo_giorni > 0 && <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'rgba(232,145,42,0.12)', color: 'var(--warn)' }}>+{fase.ritardo_giorni}gg</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: colore + '20', color: colore, fontWeight: 500 }}>{STATUS_LABEL[fase.status]}</span>
                  <span style={{ fontSize: 11, color: colore, fontWeight: 500 }}>{pct}%</span>
                </div>
              </div>
              <div style={{ position: 'relative', height: 18, borderRadius: 6, background: 'var(--ds-bg)', border: '1px solid var(--ds-border)' }}>
                <div style={{ position: 'absolute', top: 0, height: '100%', left: leftPct + '%', width: widthPct + '%', borderRadius: 5, background: colore + '20' }} />
                <div style={{ position: 'absolute', top: 0, height: '100%', left: leftPct + '%', width: (widthPct * pct / 100) + '%', borderRadius: 5, background: colore, transition: 'width 0.3s ease', minWidth: pct > 0 ? 4 : 0 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Milestone row (checklist) ──────────────────────────────────────────────

function MilestoneRow({ milestone: m, cantiereId, onRefresh }) {
  // R2 — milestone da scadenziario con data=None: NON è "scaduta" (new Date(null)=1970
  // darebbe un falso positivo). Senza data non c'è scadenza da valutare.
  const isScaduta = !m.raggiunta && !!m.data && new Date(m.data) < new Date();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--ds-border)' }}>
      <button onClick={async () => { try { await cantieri.updateMilestone(cantiereId, m.id, { raggiunta: !m.raggiunta }); if (onRefresh) onRefresh(); } catch {} }}
        title={m.raggiunta ? 'Segna come non raggiunta' : 'Segna come raggiunta'}
        style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, border: m.raggiunta ? '2px solid var(--brand)' : '2px solid ' + (isScaduta ? 'var(--warn)' : 'var(--ds-border)'), background: m.raggiunta ? 'var(--brand)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12 }}>
        {m.raggiunta ? '✓' : ''}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: 'var(--ds-text)', textDecoration: m.raggiunta ? 'line-through' : 'none', opacity: m.raggiunta ? 0.6 : 1 }}>{m.nome}</div>
        <div style={{ fontSize: 11, color: isScaduta ? 'var(--warn)' : 'var(--ds-muted)', fontStyle: m.data ? 'normal' : 'italic' }}>{m.data ? formatDate(m.data) : 'Data da definire'}{isScaduta && ' · Scaduta'}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {m.importo_sal && <div style={{ fontSize: 12, fontWeight: 500 }}>{fmtEur(m.importo_sal)}</div>}
        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: m.raggiunta ? 'rgba(46,160,132,0.12)' : (isScaduta ? 'rgba(232,145,42,0.12)' : 'var(--chip)'), color: m.raggiunta ? 'var(--brand)' : (isScaduta ? 'var(--warn)' : 'var(--ds-muted)') }}>
          {m.raggiunta ? 'Raggiunta ✓' : (isScaduta ? 'Scaduta' : 'In attesa')}
        </span>
      </div>
    </div>
  );
}

// ── Helper ─────────────────────────────────────────────────────────────────

function KpiCard({ icona, label, valore, colore, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--chip)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-card)',
      padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
      flex: '1 1 180px', minWidth: 160,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.15s ease',
    }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <span style={{ fontSize: 22 }}>{icona}</span>
      <div>
        <div style={{ fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-display)', color: colore || 'var(--ink)' }}>{valore}</div>
        <div style={{ fontSize: 11, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>{label}</div>
      </div>
    </div>
  );
}

function AlertCard({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ background: 'rgba(232,145,42,0.10)', border: '1px solid var(--warn)', borderRadius: 'var(--radius-card)', padding: '12px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--warn)', marginBottom: 6 }}>⚠️ Attenzione</div>
      {items.map((a, i) => <div key={i} style={{ fontSize: 12, color: 'var(--warn)', marginTop: 4 }}>{a}</div>)}
    </div>
  );
}

function SidebarCard({ title, children, action, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--chip)', borderRadius: 'var(--radius-card)', padding: 16, marginBottom: 16,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'border-color 0.15s ease',
      border: '1px solid transparent',
    }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = 'var(--brand)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, color }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ fontSize: 12, color: 'var(--ds-muted)' }}>{label}</span>
      <span style={{ fontSize: 12, color: color || 'var(--ds-text)' }}>{value}</span>
    </div>
  );
}

const linkBtn = { padding: '4px 10px', borderRadius: 4, border: '1px solid var(--ds-border)', background: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--ds-muted)' };
